import { RecoveryStep } from '../types';

export const RECOVERY_STEPS_MASTER: RecoveryStep[] = [
  {
    stepNumber: 1,
    id: 'step-1-email',
    title: 'Secure your primary email accounts',
    summary: 'Your email inbox is the master key to your entire digital identity and password resets.',
    whyItMatters: 'If criminals have access to your email, they can trigger password resets for your bank, myGov, superannuation, and social media accounts, intercepting confirmation emails in real time.',
    instructions: [
      'Log into your primary email provider (Gmail, Outlook, iCloud, BigPond, Yahoo) and immediately change the account password.',
      'Force sign-out on all other devices and active web sessions in your account security settings.',
      'Check your email forwarding rules and filter settings to ensure hackers have not created silent auto-forwarding rules to steal incoming mail.',
      'Verify that your backup recovery phone number and backup recovery email address belong to you.'
    ],
    relevantAgencies: [
      {
        name: 'Google Security Centre / Microsoft Security',
        website: 'https://myaccount.google.com/security',
        description: 'Review active sessions and connected third-party applications.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 2,
    id: 'step-2-passwords',
    title: 'Change compromised passwords across all services',
    summary: 'Update credentials using unique, generated passphrases for every single platform.',
    whyItMatters: 'Criminals use automated credential-stuffing tools to test leaked email/password combinations across thousands of banking, e-commerce, and government portals within minutes of a breach.',
    instructions: [
      'Do not reuse passwords across multiple services.',
      'Prioritize critical accounts: Financial institutions, myGov / ATO, Primary email, Password manager vault.',
      'Adopt strong 4-word passphrases (e.g., `kangaroo-sunset-coffee-harbour`) or use a reputable password manager (Bitwarden, 1Password, Apple Keychain).',
      'Never write passwords on physical notes attached to your computer or in unencrypted text files.'
    ],
    relevantAgencies: [
      {
        name: 'Australian Cyber Security Centre (ACSC)',
        website: 'https://www.cyber.gov.au/protect-yourself/securing-your-accounts/passphrases',
        description: 'ACSC official guidance on creating unbreakable passphrases.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 3,
    id: 'step-3-mfa',
    title: 'Enable Multi-Factor Authentication (MFA)',
    summary: 'Add an essential second verification barrier that stops over 99% of automated account takeovers.',
    whyItMatters: 'Even if a scammer discovers or cracks your password, they cannot breach your account without physical access to your hardware authenticator app or security key.',
    instructions: [
      'Enable MFA on all services that support it: myGov, Banking apps, Email, Social Media, Cloud Storage.',
      'Prefer Authenticator Apps (Google Authenticator, Microsoft Authenticator, 2FAS) or Passkeys over SMS codes where available to prevent SIM-swapping attacks.',
      'Save your one-time Emergency Backup Recovery Codes in a secure, offline location.'
    ],
    relevantAgencies: [
      {
        name: 'Cyber.gov.au Multi-Factor Guide',
        website: 'https://www.cyber.gov.au/protect-yourself/securing-your-accounts/multi-factor-authentication',
        description: 'Step-by-step instructions for turning on MFA across popular Australian platforms.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 4,
    id: 'step-4-banks',
    title: 'Contact your financial institutions & freeze cards',
    summary: 'Inform your bank fraud team immediately to dispute unauthorized transactions and protect funds.',
    whyItMatters: 'Under the Australian ePayments Code, prompt notification protects you from liability for unauthorized transactions, stopping criminal money transfers and loan drawdowns in their tracks.',
    instructions: [
      'Call your bank’s 24/7 dedicated fraud department immediately.',
      'Request temporary lock or replacement of all debit/credit cards associated with compromised accounts.',
      'Ask the fraud specialist to place high-security verbal passwords / extra security questions on your customer profile.',
      'Request a formal investigation reference number and dispute any fraudulent charges in writing.'
    ],
    relevantAgencies: [
      {
        name: 'Australian Financial Complaints Authority (AFCA)',
        phone: '1800 931 678',
        website: 'https://www.afca.org.au',
        description: 'Free independent dispute resolution if your financial institution does not fairly resolve fraud losses.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 5,
    id: 'step-5-credit-ban',
    title: 'Protect your credit file with an immediate Credit Ban',
    summary: 'Block unauthorized loans, credit cards, and mobile phone contracts from being opened in your name.',
    whyItMatters: 'When a temporary credit ban is placed, credit reporting bodies cannot disclose your credit report to credit providers without your explicit written authorization, completely paralyzing fraudulent loan applications.',
    instructions: [
      'Request an initial 21-day temporary credit ban with Equifax, Experian, or illion.',
      'By law, when you place a ban with one major Australian credit bureau, you can request that they notify the other two bureaus on your behalf.',
      'Request a free copy of your credit report from all three bureaus to check for any unapproved credit enquiries or default notices.',
      'Extend the credit ban for 12 months (free of charge) if you have a police event report or IDCARE case number.'
    ],
    relevantAgencies: [
      {
        name: 'Equifax Australia',
        phone: '138 332',
        website: 'https://www.equifax.com.au/personal/credit-ban',
        description: 'Australia’s largest credit bureau. Online ban request takes under 3 minutes.'
      },
      {
        name: 'Experian Australia',
        phone: '1300 783 684',
        website: 'https://www.experian.com.au/consumer/ban-my-credit-file',
        description: 'Major Australian credit reporting agency.'
      },
      {
        name: 'illion Australia',
        phone: '1300 734 806',
        website: 'https://www.illion.com.au/credit-ban',
        description: 'Australian consumer credit reporting body.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 6,
    id: 'step-6-replace-id',
    title: 'Replace compromised identification documents',
    summary: 'Cancel compromised Australian documents to invalidate them on the Document Verification Service (DVS).',
    whyItMatters: 'Modern Australian identity checks use the national DVS system. Changing your Driver Licence Card Number or Medicare Card Number instantly prevents stolen document numbers from passing automated verification.',
    instructions: [
      'Driver Licence: Contact your State Road Authority (Service NSW, VicRoads, QLD TMR, etc.) to get a new physical card with a new Card Number.',
      'Medicare Card: Log into myGov Medicare or call 132 011 to request a replacement card with a new 10-digit number.',
      'Passport: If stolen, report immediately to Australian Passport Office on 131 232 for immediate international cancellation.',
      'ATO / Tax File Number: Contact ATO Client Identity Support Centre on 1800 467 033 to apply identity protection safeguards.'
    ],
    relevantAgencies: [
      {
        name: 'Document Verification Service (DVS)',
        website: 'https://www.idmatch.gov.au',
        description: 'Australian Government national identity verification framework.'
      },
      {
        name: 'Services Australia Scams & ID Helpdesk',
        phone: '1800 941 126',
        website: 'https://www.servicesaustralia.gov.au',
        description: 'Dedicated support for compromised Medicare and Centrelink accounts.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 7,
    id: 'step-7-report',
    title: 'Report suspected identity theft to official authorities',
    summary: 'Establish an official paper trail with Australian law enforcement and identity support services.',
    whyItMatters: 'Having an official ReportCyber incident number or Police Event Number is required by banks, credit bureaus, and state transport departments to waive reissue fees and remove fraudulent debts from your credit history.',
    instructions: [
      'Lodge a report with ReportCyber (Cyber.gov.au) which routes your case to state/federal police.',
      'Call IDCARE (1800 595 160) for free, independent, non-profit case management and personalized recovery roadmaps.',
      'Report scam details to the National Anti-Scam Centre (Scamwatch.gov.au) to help protect other Australians.'
    ],
    relevantAgencies: [
      {
        name: 'ReportCyber (ACSC / Australian Federal Police)',
        website: 'https://www.cyber.gov.au/report-and-recover/report',
        description: 'Official Australian government cybercrime reporting portal.'
      },
      {
        name: 'IDCARE (Australia & NZ National Identity Support)',
        phone: '1800 595 160',
        website: 'https://www.idcare.org',
        description: 'Charity organization providing free, dedicated Identity Security Case Managers.'
      },
      {
        name: 'Scamwatch (ACCC National Anti-Scam Centre)',
        website: 'https://www.scamwatch.gov.au/report-a-scam',
        description: 'Australian Consumer and Competition Commission scam reporting database.'
      }
    ],
    isCompleted: false
  },
  {
    stepNumber: 8,
    id: 'step-8-ongoing-monitoring',
    title: 'Continue ongoing identity & credit monitoring',
    summary: 'Maintain continuous surveillance for lingering exposures or delayed fraudulent attempts.',
    whyItMatters: 'Identity thieves often warehouse stolen credentials for months or years after a breach before attempting to use them, waiting until the initial alert period has cooled down.',
    instructions: [
      'Keep continuous Identity & Dark Web exposure monitoring active on IDFraud.au.',
      'Set a calendar reminder to review your bank and credit statements monthly for suspicious micro-transactions.',
      'Request an updated free credit report annually from Equifax, Experian, and illion.',
      'Immediately investigate any unexpected mail, debt collection letters, or two-factor SMS alerts you did not trigger.'
    ],
    relevantAgencies: [
      {
        name: 'IDFraud.au Continuous Monitoring Centre',
        website: 'https://idfraud.au/dashboard',
        description: 'Real-time proactive breach detection and dark web scanning.'
      }
    ],
    isCompleted: false
  }
];
